import { toLocalDateKey } from '@/utils/localDateUtils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api from '@/services/api';
import MainLayout from '@/components/layout/MainLayout';
import { ReportPrintButton } from '@/components/reports/ReportPrintButton';
import { useDialog } from '@/hooks/useDialog';

interface AdjustmentRow {
  numero_contrato: string;
  propiedad: string | null;
  fecha_ejecucion: string | null;
  fecha_fin: string | null;
  inicio_prorroga: string;
  alquiler_actual: number | null;
  factor: number | null;
  nuevo_alquiler: number | null;
  aumento: number | null;
  moneda: string;
  situacion: string;
  tipo: 'renovado' | 'pendiente' | 'bloqueado';
}

interface MonthOption {
  year: number;
  month: number;
  label: string;
}

interface AdjustmentTotals {
  renewed: Record<string, number>;
  pending: Record<string, number>;
  current: Record<string, number>;
  adjusted: Record<string, number>;
  increase: Record<string, number>;
}

const columns: { key: keyof AdjustmentRow; label: string; numeric?: boolean }[] = [
  { key: 'numero_contrato', label: 'Contrato' },
  { key: 'propiedad', label: 'Propiedad' },
  { key: 'fecha_ejecucion', label: 'Ejecutada' },
  { key: 'fecha_fin', label: 'Vence' },
  { key: 'inicio_prorroga', label: 'Inicio prórroga' },
  { key: 'alquiler_actual', label: 'Alquiler actual', numeric: true },
  { key: 'factor', label: 'Factor %', numeric: true },
  { key: 'nuevo_alquiler', label: 'Nuevo alquiler', numeric: true },
  { key: 'aumento', label: 'Aumento', numeric: true },
  { key: 'situacion', label: 'Situación' },
];

const keyOf = (option: MonthOption): string => `${option.year}-${String(option.month).padStart(2, '0')}`;

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const [year, month, day] = value.slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

function formatAmount(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined) return '—';
  return `${formatNumber(value)} ${currency}`;
}

function statusClasses(status: string): string {
  if (status === 'RENOVADO') return 'border-green-200 bg-green-100 text-green-800';
  if (status === 'PENDIENTE') return 'border-blue-200 bg-blue-100 text-blue-800';
  if (status.startsWith('FALTA')) return 'border-amber-200 bg-amber-100 text-amber-800';
  return 'border-gray-200 bg-gray-100 text-gray-700';
}

export default function AdjustmentContractsReport() {
  const { showAlert } = useDialog();
  const reportRef = useRef<HTMLDivElement>(null);
  const loadedKey = useRef<string | null>(null);
  const requestId = useRef(0);
  const [options, setOptions] = useState<MonthOption[]>([]);
  const [selected, setSelected] = useState<MonthOption | null>(null);
  const [rows, setRows] = useState<AdjustmentRow[]>([]);
  const [totals, setTotals] = useState<AdjustmentTotals>({ renewed: {}, pending: {}, current: {}, adjusted: {}, increase: {} });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (month?: number, year?: number) => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const res = await api.get('/reports/adjustment-contracts', {
        params: month && year ? { month, year } : {},
      });
      if (id !== requestId.current) return;
      const payload = res.data?.data ?? res.data ?? {};
      setRows(payload.rows || []);
      setTotals({
        renewed: payload.totals?.renewed || {},
        pending: payload.totals?.pending || {},
        current: payload.totals?.current || {},
        adjusted: payload.totals?.adjusted || {},
        increase: payload.totals?.increase || {},
      });
      if (Array.isArray(payload.options) && payload.options.length > 0) {
        setOptions(payload.options);
      }
      if (payload.month) {
        const info: MonthOption = {
          year: payload.month.year,
          month: payload.month.month,
          label: payload.month.label,
        };
        loadedKey.current = keyOf(info);
        setSelected((prev) => (prev && keyOf(prev) === keyOf(info) ? prev : info));
      }
    } catch (error) {
      if (id !== requestId.current) return;
      console.error('Error loading report:', error);
      setRows([]);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selected) return;
    const key = keyOf(selected);
    if (loadedKey.current === key) return;
    void load(selected.month, selected.year);
  }, [selected, load]);

  const exportExcel = async () => {
    if (!selected) return;
    setExporting(true);
    try {
      const res = await api.get('/reports/adjustment-contracts', {
        params: { month: selected.month, year: selected.year, export: 'excel' },
      });
      const filePath = res.data?.file || res.data?.data?.file;

      if (filePath) {
        const baseURL = (api.defaults as any).baseURL || 'http://localhost/backend';
        const fileUrl = `${baseURL}/${filePath}`;

        const response = await axios.get(fileUrl, {
          responseType: 'blob',
          withCredentials: true
        });

        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contratos-reajuste-${selected.year}-${String(selected.month).padStart(2, '0')}-${toLocalDateKey(new Date())}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al exportar Excel. Por favor, intente nuevamente.',
      });
    } finally {
      setExporting(false);
    }
  };

  const renewedCount = rows.filter((row) => row.situacion === 'RENOVADO').length;
  const pendingCount = rows.filter((row) => row.situacion === 'PENDIENTE').length;
  const blockedCount = rows.length - renewedCount - pendingCount;
  const monthLabel = selected?.label || '—';
  const currencies = Array.from(new Set([
    ...Object.keys(totals.current),
    ...Object.keys(totals.adjusted),
  ]));
  const printDisabled = loading || rows.length === 0 || !selected;

  return (
    <MainLayout compact>
      <div className="w-full space-y-5">
        <Link to="/reports" className="text-sm text-blue-700">← Reportes</Link>

        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contratos para Reajuste</h1>
            <p className="mt-1 text-sm text-gray-600">
              Contratos correspondientes al factor de {monthLabel}: renovados y pendientes.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <ReportPrintButton
              title="Contratos para Reajuste"
              subtitle={`Factor de ${monthLabel}`}
              containerRef={reportRef}
              disabled={printDisabled}
            />
            <ReportPrintButton
              variant="pdf"
              title="Contratos para Reajuste"
              subtitle={`Factor de ${monthLabel}`}
              containerRef={reportRef}
              disabled={printDisabled}
            />
            <button
              className="px-4 py-2 bg-green-700 text-white rounded hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              onClick={exportExcel}
              disabled={loading || exporting || rows.length === 0 || !selected}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {exporting ? 'Exportando...' : 'Exportar Excel'}
            </button>
          </div>
        </div>

        {/* Month selector */}
        {options.length > 0 && (
          <div className="overflow-x-auto border-b border-slate-200" role="tablist" aria-label="Mes del factor de reajuste">
            <div className="flex gap-6 whitespace-nowrap">
              {options.map((option) => {
                const active = selected ? keyOf(option) === keyOf(selected) : false;
                return (
                  <button
                    key={keyOf(option)}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setSelected(option)}
                    className={`border-b-2 px-1 pb-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                      active
                        ? 'border-primary text-primary'
                        : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div ref={reportRef} className="bg-white border rounded-lg p-3 sm:p-4">
          {loading ? (
            <p role="status" className="py-12 text-center text-gray-600">Cargando reporte…</p>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mb-4 text-5xl">✅</div>
              <h3 className="mb-2 text-lg font-medium text-gray-900">Sin renovaciones para {monthLabel}</h3>
              <p className="text-gray-600">No hay renovaciones ni candidatos pendientes correspondientes al factor de este mes.</p>
              <p className="mt-2 text-xs text-gray-500">
                Los renovados se agrupan por el factor aplicado y los pendientes por el mes de vencimiento del contrato.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{renewedCount}</span> renovada{renewedCount !== 1 ? 's' : ''}
                  {' · '}
                  <span className="font-semibold">{pendingCount}</span> pendiente{pendingCount !== 1 ? 's' : ''}
                  {blockedCount > 0 && (
                    <>
                      {' · '}
                      <span className="font-semibold">{blockedCount}</span> con bloqueo
                    </>
                  )}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-500">
                      {columns.map((column) => (
                        <th
                          key={column.key}
                          scope="col"
                          className={`p-2 font-medium whitespace-nowrap ${column.numeric ? 'text-right' : 'text-left'}`}
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={`${row.numero_contrato}-${row.tipo}-${index}`} className="transition-colors hover:bg-gray-50">
                        {columns.map((column) => {
                          const value = row[column.key];
                          const numeric = column.numeric ? 'text-right whitespace-nowrap tabular-nums' : 'text-left';

                          if (column.key === 'situacion') {
                            return (
                              <td key={column.key} className={`p-2 ${numeric}`}>
                                <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusClasses(row.situacion)}`}>
                                  {row.situacion}
                                </span>
                              </td>
                            );
                          }

                          if (column.key === 'fecha_ejecucion' || column.key === 'fecha_fin' || column.key === 'inicio_prorroga') {
                            return <td key={column.key} className={`p-2 ${numeric} whitespace-nowrap`}>{formatDate(row[column.key] as string | null)}</td>;
                          }

                          if (column.key === 'alquiler_actual' || column.key === 'nuevo_alquiler' || column.key === 'aumento') {
                            return <td key={column.key} className={`p-2 ${numeric}`}>{formatAmount(row[column.key] as number | null, row.moneda)}</td>;
                          }

                          if (column.key === 'factor') {
                            return (
                              <td key={column.key} className={`p-2 ${numeric}`}>
                                {row.factor === null ? '—' : `${formatNumber(row.factor)} %`}
                              </td>
                            );
                          }

                          return <td key={column.key} className={`p-2 ${numeric}`}>{String(value ?? '')}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                  {currencies.length > 0 && (
                    <tfoot className="border-t border-gray-500">
                      {currencies.map((currency) => (
                        <tr key={currency}>
                          {columns.map((column, index) => {
                            if (index === 0) {
                              return <td key={column.key} className="p-2 text-left whitespace-nowrap font-medium">Total {currency}</td>;
                            }
                            if (column.key === 'alquiler_actual') {
                              const total = totals.current[currency];
                              return <td key={column.key} className="p-2 text-right whitespace-nowrap tabular-nums font-medium">{total !== undefined ? formatNumber(total) : ''}</td>;
                            }
                            if (column.key === 'nuevo_alquiler') {
                              const total = totals.adjusted[currency];
                              return <td key={column.key} className="p-2 text-right whitespace-nowrap tabular-nums font-medium">{total !== undefined ? formatNumber(total) : ''}</td>;
                            }
                            if (column.key === 'aumento') {
                              const total = totals.increase[currency];
                              return <td key={column.key} className="p-2 text-right whitespace-nowrap tabular-nums font-medium">{total !== undefined ? formatNumber(total) : ''}</td>;
                            }
                            return <td key={column.key} className="p-2" />;
                          })}
                        </tr>
                      ))}
                    </tfoot>
                  )}
                </table>
              </div>

              <p className="mt-3 text-xs text-gray-600">
                «RENOVADO»: el cron ya aplicó la renovación. «PENDIENTE»: tiene factor disponible y espera la ejecución del cron.
                El cron también recupera los vencimientos pendientes del mes en curso.
                Las filas con otra situación no se renuevan automáticamente. La pestaña corresponde al mes del factor de reajuste,
                determinado por el vencimiento del contrato; «Ejecutada» indica cuándo corrió el cron. «Alquiler actual» muestra el importe anterior al reajuste.
                Los totales incluyen las renovadas y las pendientes con factor disponible.
              </p>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
